/**
 * Multilingual content helpers for the Stoic Journal.
 *
 * Every article keeps its user-facing fields (title, excerpt, body,
 * meta_description, author) as `{ en, nl, de?, fr?, es? }` objects.
 * Fallback strategy, consistent across every consumer:
 *
 *   1. Prefer the requested language.
 *   2. Fall back to English, then Dutch (never show nothing).
 *   3. Return "" if the field is missing entirely.
 *
 * The public site falls back silently to another language — visitors always
 * see content. The Admin surfaces the translation status via
 * `translationStatus(article)` so the founder knows where NL is missing.
 */

/** Pull the best value for `lang` from a localized field, with fallback. */
export function pickLocale(field, lang) {
  if (!field) return "";
  if (typeof field === "string") return field;
  const primary = field[lang];
  if (primary && String(primary).trim()) return primary;
  for (const code of ["en", "nl"]) {
    if (code === lang) continue;
    const v = field[code];
    if (v && String(v).trim()) return v;
  }
  return "";
}

/** Return whether an article has content for both languages, one only, or none. */
export function translationStatus(article) {
  if (!article) return "none";
  // Base status on the title — if a title is missing the article isn't usable
  // in that language anyway. Body/excerpt are extra signal.
  const t = article.title || {};
  const hasEn = !!(t.en && String(t.en).trim());
  const hasNl = !!(t.nl && String(t.nl).trim());
  if (hasEn && hasNl) return "both";
  if (hasEn) return "en_only";
  if (hasNl) return "nl_only";
  return "none";
}
